import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, CalendarDays, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";

type GoogleStatus = {
  provider: string;
  connected: boolean;
  status: "connected" | "needs_reauth" | "disconnected";
  account_email?: string | null;
};

type CalendarStatus = {
  provider: string;
  connected: boolean;
  status: "connected" | "needs_scope" | "needs_reauth" | "disconnected";
  has_scope: boolean;
  account_email?: string | null;
};

export function ConnectionsCard() {
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [calendar, setCalendar] = useState<CalendarStatus | null>(null);
  const [configured, setConfigured] = useState<boolean>(true);
  const [calendarConfigured, setCalendarConfigured] = useState<boolean>(true);
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/connections");
      setGoogle(r.data.google);
      setCalendar(r.data.calendar);
      setConfigured(r.data.configured);
      setCalendarConfigured(r.data.calendar_configured);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Surface the OAuth redirect outcome (?google=connected|error) then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const g = params.get("google");
    if (g === "connected") {
      toast.success("Google account updated");
      navigate("/settings", { replace: true });
      load();
    } else if (g === "error") {
      toast.error("Couldn't update your Google connection. Please try again.");
      navigate("/settings", { replace: true });
    }
  }, [location.search, navigate, load]);

  const redirectTo = async (path: string) => {
    setBusy(true);
    try {
      const r = await api.post(path);
      window.location.href = r.data.authorize_url;
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  const connectEmail = () => redirectTo("/connections/google/start");
  const connectCalendar = () => redirectTo("/connections/google/calendar/start");

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/connections/google/disconnect");
      toast.success("Google account disconnected");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const emailConnected = google?.connected;
  const emailNeedsReauth = google?.status === "needs_reauth";
  const calConnected = calendar?.connected;
  const calNeedsScope = calendar?.status === "needs_scope";

  return (
    <section className="rounded-3xl border border-border bg-card p-5" data-testid="connections-card">
      <h2 className="text-sm font-semibold">Connections</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Connect your Google account so {`Remy`} can read your email and calendar. Read-only — {`Remy`} can’t send, change, or delete anything.
      </p>

      {/* Email */}
      <div className="mt-4 flex items-center justify-between rounded-2xl bg-secondary/40 px-4 py-3" data-testid="connection-email">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">Email</div>
            <div className="text-xs text-muted-foreground" data-testid="connection-email-status">
              {emailConnected ? (
                <span className="inline-flex items-center gap-1 text-primary">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Connected{google?.account_email ? ` · ${google.account_email}` : ""}
                </span>
              ) : emailNeedsReauth ? (
                <span className="inline-flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Reconnect needed
                </span>
              ) : (
                "Not connected"
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {emailConnected ? (
            <Button variant="outline" size="sm" className="rounded-full" onClick={disconnect}
              disabled={busy} data-testid="connection-disconnect">
              Disconnect
            </Button>
          ) : (
            <Button size="sm" className="rounded-full" onClick={connectEmail}
              disabled={busy || !configured}
              data-testid={emailNeedsReauth ? "connection-reconnect" : "connection-connect"}>
              {emailNeedsReauth ? "Reconnect" : "Connect"}
            </Button>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="mt-3 flex items-center justify-between rounded-2xl bg-secondary/40 px-4 py-3" data-testid="connection-calendar">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="text-sm font-medium">Calendar</div>
            <div className="text-xs text-muted-foreground" data-testid="connection-calendar-status">
              {calConnected ? (
                <span className="inline-flex items-center gap-1 text-primary">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Connected{calendar?.account_email ? ` · ${calendar.account_email}` : ""}
                </span>
              ) : calNeedsScope ? (
                <span className="inline-flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Needs calendar permission
                </span>
              ) : (
                "Not connected"
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {calConnected ? (
            <span className="text-xs text-muted-foreground">Read-only</span>
          ) : (
            <Button size="sm" variant={calNeedsScope ? "default" : "outline"} className="rounded-full"
              onClick={connectCalendar} disabled={busy || !calendarConfigured}
              data-testid={calNeedsScope ? "connection-calendar-add" : "connection-calendar-connect"}>
              {calNeedsScope ? "Add access" : "Connect"}
            </Button>
          )}
        </div>
      </div>

      {!configured && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="connection-unavailable">
          Google connection isn’t available yet.
        </p>
      )}
    </section>
  );
}
